import type { Message } from '@/types';

export type GroupedChatItem =
  | { type: 'message'; data: Message }
  | {
      type: 'tool-pair';
      data: { call: Message | null; response: Message | null };
    };

/**
 * Pair `tool call` messages with their matching `tool response` by
 * `toolUseId`. Used by both the AIAssistant chat and the Hunts drawer
 * so the ChatMessage renderer can display each pair as one unit.
 *
 * Callers are responsible for any prior filtering (e.g. dropping empty
 * planner-output messages in AIAssistant).
 */
export function groupMessagesByToolUseId(
  messages: Message[]
): GroupedChatItem[] {
  const grouped: GroupedChatItem[] = [];
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const toolUseId = (msg as { toolUseId?: string }).toolUseId;

    if (msg.role === 'tool call') {
      const response =
        messages.find(
          (m, idx) =>
            idx > i &&
            m.role === 'tool response' &&
            (m as { toolUseId?: string }).toolUseId === toolUseId
        ) ?? null;
      grouped.push({ type: 'tool-pair', data: { call: msg, response } });
      continue;
    }

    if (msg.role === 'tool response') {
      const alreadyPaired = grouped.some(
        g =>
          g.type === 'tool-pair' &&
          (g.data.response as { toolUseId?: string } | null)?.toolUseId ===
            toolUseId
      );
      if (!alreadyPaired) {
        grouped.push({
          type: 'tool-pair',
          data: { call: null, response: msg },
        });
      }
      continue;
    }

    grouped.push({ type: 'message', data: msg });
  }
  return grouped;
}
